/**
 * Prolonged disruption — force majeure, armed conflict, and airspace closed
 * for an extended or indefinite period.
 *
 * The Crisis Desk's playbooks are about the first quarter of an hour. These
 * are the events that do not end in an hour, and the distinction is not one of
 * degree. A runway closed by a disabled aircraft is an operational problem with
 * an operational answer. Airspace closed by the State for three months is a
 * solvency problem wearing an operational costume: nobody diverts, nobody is
 * bussed, and the question stops being "how do we reopen" and becomes "what do
 * we still own when we are allowed to".
 *
 * So this module is built round the two things an emergency plan does not hold:
 *
 * **Duration bands, not minutes.** Every scenario is staged 0–72 hours, 72
 * hours to four weeks, one to three months, and beyond three months or
 * unknown. The last band is the one that matters and the one usually missing:
 * an aerodrome emergency plan under GACAR Part 139 Subpart F and ICAO Annex 14
 * Vol I §9.1 is written for an event with an end, and it says nothing about the
 * month in which the operator decides whether to keep its workforce.
 *
 * **What it costs to wait.** Each posture is priced against the finance the
 * platform actually holds — the daily operating cost, the daily revenue, the
 * cash balance and the facility — so "reduce to minimum" carries the date the
 * cash runs out if the airport sits there. A continuity plan with no runway
 * date in it is a list of good intentions.
 *
 * ── what is a record and what is an assumption ──────────────────────────
 *
 * The figures come from `Finance`, whose `basis` says whether it was read
 * from a ledger, entered by a named person, or is demonstration data, and that
 * basis is carried onto every number here.
 *
 * The posture factors are NOT records. There is no way to read from an estate
 * what it will cost to run at minimum posture, because it has never done it.
 * They are planning assumptions, declared in `POSTURES` with the reasoning
 * beside each, marked `assumed` everywhere they are used, and shown to the
 * reader as assumptions. Substituting a plausible number for a measured one is
 * the failure this platform exists to avoid, and it would be easiest to commit
 * exactly here, where nobody can check.
 *
 * The scenarios themselves are proposed, not adopted. Nothing in this file has
 * been through an accountable manager, and the desk says so. See
 * [[kpi-targets-proposed]] for the same treatment of the benchmark targets.
 *
 * Pure. The page gathers, the desk decides.
 */
import { t, tn } from "../i18n";
import type { SourceRef } from "./decision";
import type { Finance } from "./finance";
import { money } from "./intelligence";
import type { DocumentRow } from "./library";
import type { ComplianceReport } from "./compliance";

/* ── postures ─────────────────────────────────────────────────────────── */

export type PostureKey = "full" | "reduced" | "minimum" | "closed" | "care";

export interface Posture {
  key: PostureKey;
  name: string;
  what: string;
  /** operating cost at this posture, as a share of the normal daily cost */
  opexFactor: number;
  /** revenue at this posture, as a share of the normal daily revenue */
  revenueFactor: number;
  /** why the factor is what it is — the reasoning, not a citation */
  basis: string;
}

/**
 * The five postures, and the share of cost and revenue each carries.
 *
 * Every number below is an assumption and is presented as one. They are not
 * arbitrary — each says what it is reasoning from — but an airport that has
 * never run at minimum posture has no record of what minimum posture costs,
 * and dressing a planning figure as a measurement would be the one thing this
 * desk must not do.
 *
 * An operator should replace these with their own before the plan is adopted.
 * They are in one table, named, so that is a single edit.
 */
export const POSTURES: Posture[] = [
  {
    key: "full",
    name: "Full operation",
    what: "every stand, every lane, the published schedule",
    opexFactor: 1,
    revenueFactor: 1,
    basis: "the platform's own figures, unadjusted",
  },
  {
    key: "reduced",
    name: "Reduced operation",
    what: "one runway, a single terminal pier, screening lanes to demand, night movements suspended",
    opexFactor: 0.78,
    revenueFactor: 0.45,
    basis:
      "most cost is fixed — the pavement, the lighting, the fire cover and the establishment do not halve when the traffic does; revenue falls faster than cost, which is the whole difficulty",
  },
  {
    key: "minimum",
    name: "Minimum safe operation",
    what: "diversion and State-flight capability only: one runway available on notice, RFFS at the declared category, no scheduled traffic",
    opexFactor: 0.55,
    revenueFactor: 0.08,
    basis:
      "the regulatory floor plus the crews needed to hold it; aeronautical revenue is effectively gone and what remains is property and concession income under whatever relief is negotiated",
  },
  {
    key: "closed",
    name: "Closed to traffic",
    what: "aerodrome closed by NOTAM, certificate maintained, no movements",
    opexFactor: 0.38,
    revenueFactor: 0.03,
    basis:
      "security of the perimeter, the certificate, insurance, statutory inspection and enough engineering to stop the estate deteriorating",
  },
  {
    key: "care",
    name: "Care and maintenance",
    what: "the estate preserved, the certificate surrendered or suspended, a skeleton custodial team",
    opexFactor: 0.22,
    revenueFactor: 0.01,
    basis:
      "preservation only. Cheapest to hold and the most expensive to leave: recovering from it is a recertification, not a reopening",
  },
];

export const POSTURE_BY_KEY: Record<PostureKey, Posture> = Object.fromEntries(
  POSTURES.map((p) => [p.key, p]),
) as Record<PostureKey, Posture>;

/* ── the shape of a prolonged scenario ────────────────────────────────── */

export interface ContinuityStage {
  key: string;
  band: string;
  fromDays: number;
  /** null means "and beyond, or unknown" — the band this desk exists for */
  toDays: number | null;
  posture: PostureKey;
  /** the decision this band forces, and it is forced whether or not it is taken */
  decide: string;
  operations: string[];
  /** the workforce */
  people: string[];
  /** operating spend */
  spend: string[];
  /** the capital programme */
  projects: string[];
  /** what is renegotiated, and with whom */
  contracts: string[];
  /** what cannot be undone cheaply once done — said before it is done */
  irreversible?: string;
}

export interface RestartPhase {
  key: string;
  name: string;
  /** working days before traffic this phase must begin */
  leadDays: number;
  gate: string;
  actions: string[];
  /** the platform record that shows it was done */
  evidence: string;
}

export interface FloorItem {
  what: string;
  why: string;
  reference: string;
}

export interface ContinuityScenario {
  key: string;
  name: string;
  trigger: string;
  /** what makes it a continuity event rather than an emergency */
  character: string;
  holds: string[];
  /** where the aerodrome emergency plan stops and this begins */
  handover: string;
  stages: ContinuityStage[];
  restart: RestartPhase[];
  floor: FloorItem[];
}

/* ── the regulatory floor ─────────────────────────────────────────────── */

/**
 * What continues even when nothing flies.
 *
 * The expensive misunderstanding about closing an aerodrome is that closing it
 * stops the obligations. It stops the traffic. An aerodrome that has shut its
 * gates is still a certificated aerodrome with a perimeter, an obstacle
 * environment, a wildlife population that has just been given the run of the
 * place, and pavement that deteriorates on a clock rather than on a movement
 * count. Every item below is a cost that survives a full shutdown, and each is
 * the thing a cost-cutting exercise reaches for first because nothing visibly
 * breaks the week it is cut.
 *
 * References are given so a reader can check them against the issue in force
 * at their own aerodrome. They are pointers to the right clause, not legal
 * advice, and the authority's own text governs.
 */
export const REGULATORY_FLOOR: FloorItem[] = [
  {
    what: "Promulgate the change in the level of service, and keep it current",
    why: "a closure, a downgraded rescue and fire fighting category, a withdrawn runway — each has to reach crews through NOTAM and the AIP before it is relied upon. An aerodrome that is closed in fact and open on paper is the most dangerous state available.",
    reference: "ICAO Annex 15 / PANS-AIM (Doc 10066); ICAO Annex 14 Vol I §2.1",
  },
  {
    what: "Hold the aerodrome certificate, or surrender it deliberately",
    why: "the certificate carries conditions that do not lapse because the traffic did. Letting it fail by inattention turns a reopening into a recertification, which is the single most expensive way to save money.",
    reference: "ICAO Annex 14 Vol I §1.4; GACAR Part 139 Subpart B",
  },
  {
    what: "Rescue and fire fighting at the promulgated category whenever a movement is permitted",
    why: "the category may be reduced, but only if it is promulgated before it is reduced. A diversion arriving on the published category and finding a lower one is the failure this clause exists to prevent.",
    reference: "ICAO Annex 14 Vol I §9.2; GACAR Part 139 Subpart D",
  },
  {
    what: "Security of the perimeter and the movement area",
    why: "an empty airfield is a more attractive target, not a less attractive one, and unauthorised access to a movement area is an aviation security matter whether or not anything is flying.",
    reference: "ICAO Annex 17; the aerodrome's own security programme",
  },
  {
    what: "Wildlife hazard management",
    why: "movements disperse wildlife. Removing them increases the hazard, and the hazard has to be back under control before the first aircraft returns, not after it.",
    reference: "ICAO Annex 14 Vol I §9.4; GACAR Part 139 wildlife hazard assessment",
  },
  {
    what: "Obstacle limitation surfaces and the safeguarding of the approaches",
    why: "cranes, masts and construction do not pause because the airport did, and an obstacle permitted during a closure is still an obstacle on the day it reopens.",
    reference: "ICAO Annex 14 Vol I Chapter 4",
  },
  {
    what: "Preservation of pavement, lighting and drainage",
    why: "deterioration is a function of time, weather and water, not of movements. Deferring it through a closure converts an operating cost into a capital one at an unfavourable rate.",
    reference: "ICAO Doc 9137 Part 9; the aerodrome manual's maintenance schedule",
  },
  {
    what: "Insurance, and the war and allied perils position in particular",
    why: "cover is written against operations and premises. A change of use, a period of closure or the presence of military activity can each void or reprice it, and finding out at the claim is finding out too late.",
    reference: "the policy wording; review with the broker on entering any stage below full operation",
  },
  {
    what: "Records, competence and currency of the people kept",
    why: "licenses, medicals, ratings and recurrent training lapse on a calendar. A team stood down for four months and recalled in a week is a team that cannot lawfully work in that week.",
    reference: "the training matrix; GACAR Part 139 personnel competency requirements",
  },
];

/* ── the restart, shared in shape and named per scenario ──────────────── */

function restartPhases(extra: Partial<Record<string, string[]>> = {}): RestartPhase[] {
  return [
    {
      key: "decide",
      name: "Decision to reopen",
      leadDays: 45,
      gate: "the constraint that closed the aerodrome is lifted, or a date for it is credible enough to spend against",
      actions: [
        "Name the reopening date and the posture it reopens at; publish it internally before externally.",
        "Reconvene the crisis executive as a restart executive, with the same names, so nothing is handed over.",
        "Reopen the risk register: every deferral made during the shutdown becomes an item with an owner.",
        "Confirm the insurance position for the posture being resumed, in writing, before any commitment.",
        ...(extra.decide ?? []),
      ],
      evidence: "the decision log entry, and the board minute behind it",
    },
    {
      key: "people",
      name: "People and competence",
      leadDays: 40,
      gate: "every licence, medical, rating and recurrent training needed for the opening posture is current or has a booked date before it",
      actions: [
        "Run the training matrix against the opening roster; the gap is the recall schedule.",
        "Recall in competence order, not seniority order — the constraint is currency, not headcount.",
        "Re-induct everyone, including returners: four months away is long enough to lose an airfield driving permit in practice as well as on paper.",
        "Rebuild the duty rosters for the opening posture and prove they hold for eight weeks without discretionary overtime.",
        ...(extra.people ?? []),
      ],
      evidence: "the training records and the competence matrix on the platform",
    },
    {
      key: "estate",
      name: "Estate and serviceability",
      leadDays: 30,
      gate: "the movement area, the lighting and the systems meet the standard for the opening posture, proven by inspection rather than by assertion",
      actions: [
        "Full pavement condition survey and friction testing; compare with the last survey before closure and account for every change.",
        "Energise and test every AGL circuit, and hold a night serviceability check with the tower.",
        "Run every system that was rested: baggage, screening, lifts, stands, fuel. A system idle for months fails on its first day, not its tenth.",
        "Wildlife hazard assessment repeated from scratch — the population has changed and the previous assessment describes a different airfield.",
        "Clear the deferred maintenance backlog accrued during the shutdown, or carry each item as a named, accepted risk.",
        ...(extra.estate ?? []),
      ],
      evidence: "inspections, work orders, readings and the condition model on the platform",
    },
    {
      key: "regulator",
      name: "Regulator and promulgation",
      leadDays: 21,
      gate: "the authority has what it needs for the posture being resumed, and the aeronautical information is correct on the day",
      actions: [
        "Submit the changes made during the shutdown: level of service, category, procedures, organisation.",
        "Agree the verification the authority wants — inspection, audit or exercise — and book it early; this is the step that slips.",
        "Run the aerodrome emergency exercise before traffic, not after: the plan has not been tested by a team that has not worked together for months.",
        "Cancel or amend every NOTAM raised for the closure, on the day, in the right order.",
        ...(extra.regulator ?? []),
      ],
      evidence: "the compliance register, the exercise record and the document library",
    },
    {
      key: "commercial",
      name: "Commercial and capacity",
      leadDays: 14,
      gate: "the airlines, the handlers, the concessions and the contractors can be there on the day, at the volume promised",
      actions: [
        "Confirm slots and the opening schedule with each carrier; publish the capacity actually available, not the capacity hoped for.",
        "Restore handling, fuel and catering contracts, including any suspended during the shutdown, with lead times written down.",
        "Reopen concessions in step with traffic; an empty terminal full of open shops is a second loss.",
        "Restart the capital programme in the order agreed at stand-down, and say publicly which schemes are not coming back.",
        ...(extra.commercial ?? []),
      ],
      evidence: "the capacity model, the contract register and the capital programme",
    },
    {
      key: "prove",
      name: "Proving",
      leadDays: 3,
      gate: "the airport has run a full day at the opening posture without traffic on it",
      actions: [
        "Proving day: every position staffed, every system live, a simulated schedule run end to end.",
        "Exercise one emergency scenario on the day, with the people who will hold it.",
        "Snag list closed or accepted by name before the first commercial movement.",
        "Open at a deliberately reduced schedule and hold it for two weeks before adding.",
        ...(extra.prove ?? []),
      ],
      evidence: "the proving-day record and the snag list, both on the platform",
    },
  ];
}

/* ── the scenarios ────────────────────────────────────────────────────── */

const AEP_HANDOVER =
  "The aerodrome emergency plan holds the first hours and assumes an end. It hands over here at the point the event acquires no end date — in practice when the executive cannot name a reopening date, which is usually earlier than anyone admits.";

export const CONTINUITY_SCENARIOS: ContinuityScenario[] = [
  {
    key: "force_majeure",
    name: "Force majeure",
    trigger:
      "an event outside the operator's control that suspends performance: natural disaster, epidemic, State action, sanctions, a supply failure with no substitute",
    character:
      "obligations are suspended rather than breached, and the question is which of them the operator wants suspended. Relief is not automatic — it is claimed, in writing, within a notice period, and the notice period is usually short.",
    holds: [
      "Chief executive",
      "Chief financial officer",
      "Legal counsel",
      "Chief operating officer",
      "Head of communications",
      "Insurance broker",
    ],
    handover: AEP_HANDOVER,
    stages: [
      {
        key: "0-72h",
        band: "0–72 hours",
        fromDays: 0,
        toDays: 3,
        posture: "reduced",
        decide:
          "Is this force majeure under our contracts, and have we served notice? The clock is contractual, not operational.",
        operations: [
          "Stand up the emergency operations centre on the aerodrome emergency plan; it runs the first hours unchanged.",
          "Establish what is actually unavailable — surfaces, systems, people, supply — and what merely looks unavailable.",
          "Publish the level of service by NOTAM within the hour, and keep it current as it changes.",
          "Protect people and assets first; commercial decisions in this band are reversible and should be.",
        ],
        people: [
          "Account for everyone. Nothing else starts until that is complete.",
          "Suspend non-essential travel and training; keep currency training, which lapses.",
          "Open a single internal channel and use it daily, even when there is nothing new — silence is filled with worse.",
        ],
        spend: [
          "Freeze discretionary spend and all new commitments above a stated threshold, by name, today.",
          "Stop purchase orders not yet delivered where cancellation is free; list those where it is not.",
        ],
        projects: [
          "Pause mobilisation on any scheme not yet started. Do not cancel yet — cancellation is expensive and this band is too early to know.",
        ],
        contracts: [
          "Serve force majeure notice under every contract that requires it, within its notice period. This is the single time-critical commercial act of the first 72 hours.",
          "Notify insurers and the broker; record the date and time of notification.",
          "Ask, do not assume: obtain each counterparty's position in writing rather than inferring it from silence.",
        ],
        irreversible: "A missed contractual notice period cannot be recovered later, however reasonable the reason.",
      },
      {
        key: "72h-4w",
        band: "72 hours – 4 weeks",
        fromDays: 3,
        toDays: 28,
        posture: "reduced",
        decide: "What is the credible duration, and what does the cash position permit us to hold while we find out?",
        operations: [
          "Consolidate to the smallest footprint that serves the traffic that remains: one pier, one runway, screening to demand.",
          "Close what is genuinely unused rather than running it at low load — a half-used terminal costs nearly a full one.",
          "Rebuild the roster around the reduced posture instead of carrying the full one at low utilisation.",
        ],
        people: [
          "Stop recruitment; convert offers not yet accepted into deferrals with a stated review date.",
          "Use leave balances, flexible hours and voluntary unpaid leave before anything structural.",
          "Keep safety-critical and licence-currency roles whole. They are the constraint on reopening and the cheapest thing to have kept.",
        ],
        spend: [
          "Rebuild the operating budget bottom-up at the reduced posture rather than applying a percentage cut to the existing one.",
          "Cut by area against the ledger's own split, so each reduction names a line somebody owns.",
          "Renegotiate utilities and waste to actual consumption; standing charges on an empty terminal are pure loss.",
        ],
        projects: [
          "Sort the capital programme into three: mandatory (safety, regulatory, already-committed), deferrable, and cancellable.",
          "Defer the deferrable with a stated review date. A deferral without a date becomes a cancellation nobody decided.",
          "Keep any scheme whose deferral costs more than its completion — part-built pavement and open excavations deteriorate.",
        ],
        contracts: [
          "Open renegotiation with the largest suppliers first; the top ten usually carry most of the spend.",
          "Seek volume-linked terms rather than headline discounts — the volume is the thing that changed.",
          "Convert fixed availability charges to consumption where the counterparty will, and pay for the option to restore.",
          "Agree reinstatement lead times in writing while renegotiating. The cost of a contract is not only its price; it is how long it takes to get back.",
        ],
      },
      {
        key: "1-3m",
        band: "1–3 months",
        fromDays: 28,
        toDays: 90,
        posture: "minimum",
        decide:
          "Do we hold the workforce or release it? This is the decision that determines what reopening costs, and it is taken on incomplete information by definition.",
        operations: [
          "Move to minimum safe operation: diversion and State-flight capability, no scheduled traffic.",
          "Mothball terminal zones formally — isolated, secured, inspected on a schedule, not simply locked.",
          "Keep one runway available on notice rather than all surfaces at readiness.",
        ],
        people: [
          "Consult formally on structural reductions, to the timetable the law requires; the timetable is often longer than the cash allows, which is why it starts here and not later.",
          "Prefer short-time working and salary deferral to redundancy where the return is plausible: rehiring a licensed workforce takes longer than raising the money to keep it.",
          "Protect the reopening cadre by name — the people without whom a reopening cannot be gated.",
          "Fund currency training for everyone retained. A retained employee whose licence has lapsed is a cost with no capability.",
        ],
        spend: [
          "Reforecast against the minimum posture and publish the runway date to the board monthly.",
          "Close the smallest sites and offices outright rather than reducing them.",
          "Challenge every remaining line against the regulatory floor: if it is not on the floor and not revenue-generating, it needs an owner's argument to survive.",
        ],
        projects: [
          "Cancel the cancellable and take the cancellation cost now, while the counterparty is also motivated.",
          "Renegotiate committed schemes to longer programmes at lower monthly draw rather than stopping them dead.",
          "Preserve design and consent work — it is cheap to hold, expensive to redo, and it is what allows a fast restart.",
        ],
        contracts: [
          "Renegotiate concession and property leases to turnover-based rent with a floor, in exchange for term.",
          "Restructure handling and fuel agreements to standby rates with defined reactivation notice.",
          "Approach lenders before a covenant is breached rather than after; a waiver sought early is a different conversation.",
          "Review every contract's termination cost against its reinstatement cost, and record both.",
        ],
        irreversible:
          "Structural redundancy, a surrendered lease and a cancelled scheme are all far more expensive to reverse than to make. Each should be taken with the reopening cost written down beside it.",
      },
      {
        key: "3m+",
        band: "beyond 3 months, or no known end",
        fromDays: 90,
        toDays: null,
        posture: "closed",
        decide:
          "Do we hold the certificate and the estate against a reopening we cannot date, or preserve capital and accept that reopening becomes a project rather than a decision?",
        operations: [
          "Close to traffic by NOTAM and hold the certificate, or move to care and maintenance deliberately — the two are different decisions with different exits.",
          "Put the estate on a preservation regime: pavement, drainage, lighting, fuel systems and fixed plant on a documented cycle.",
          "Retain the ability to reopen at minimum posture on a stated notice period, and state it honestly — usually weeks, not days.",
        ],
        people: [
          "Hold a custodial establishment: security, engineering preservation, compliance, finance, and the reopening cadre.",
          "Keep the competence matrix alive for everyone retained; recurrent training continues through a closure or the reopening is gated by it.",
          "Maintain contact with released staff and a right of first refusal — the licensed labour market is small and slow.",
        ],
        spend: [
          "Rebase to the closed posture and hold it; a closure run on a series of monthly cuts costs more than one run on a plan.",
          "Move to a preservation budget with its own approval route, so preservation is not cut as though it were discretionary.",
          "Report runway date, burn and the reopening cost estimate to the board together, every month. One without the others invites the wrong decision.",
        ],
        projects: [
          "Stop the capital programme apart from preservation and anything whose stopping causes deterioration.",
          "Convert what remains to a shelf-ready position: designs complete, consents live, procurement drafted, nothing committed.",
          "Say publicly which schemes are not returning. An undead programme consumes management attention and supplier goodwill for nothing.",
        ],
        contracts: [
          "Move suppliers to dormancy agreements: near-zero standing cost, defined reactivation notice and a fixed reactivation price.",
          "Renegotiate for term rather than for price — length is worth more than rate to a counterparty who also wants to survive.",
          "Keep one contracted route back for every critical service. A service with no contracted route back is a reopening dependency nobody has priced.",
          "Review insurance for the closed and unoccupied position specifically; standard wordings often exclude it.",
        ],
        irreversible:
          "Surrendering the certificate, releasing the licensed workforce and disposing of specialist plant each convert a reopening into a recertification. Any of them may still be right — but each should be signed for by name, with the reopening cost stated.",
      },
    ],
    restart: restartPhases({
      decide: ["Confirm that the force majeure event has ended in fact and in contract; the two dates differ."],
      commercial: ["Reinstate suspended contracts in the notice order agreed at stand-down, longest lead first."],
    }),
    floor: REGULATORY_FLOOR,
  },
  {
    key: "conflict",
    name: "Armed conflict or war in the region",
    trigger:
      "hostilities affecting the State or its neighbours: airspace restricted or closed, overflight withdrawn, insurance repriced or withdrawn, the aerodrome required for State or military use",
    character:
      "the aerodrome may be serviceable, staffed and willing, and still unable to operate. Decisions are made under a security constraint, on information that is incomplete by design, and the safety of people displaces every commercial consideration.",
    holds: [
      "Chief executive",
      "Chief operating officer",
      "Head of security",
      "State and military liaison",
      "Legal counsel",
      "Chief financial officer",
      "Head of communications",
    ],
    handover: AEP_HANDOVER,
    stages: [
      {
        key: "0-72h",
        band: "0–72 hours",
        fromDays: 0,
        toDays: 3,
        posture: "minimum",
        decide: "Are our people safe where they are, and who is authorised to speak to the State?",
        operations: [
          "Account for every person on site; move non-essential staff off it.",
          "Take the level of service from the State's instruction, not from what the aerodrome could physically do.",
          "Raise security to the level directed and hold it; expect it to be directed rather than chosen.",
          "Assume communications and information are contested: verify instructions through a named channel before acting on them.",
        ],
        people: [
          "Safety of people before continuity of service, explicitly and in writing, so nobody has to infer it under pressure.",
          "Identify staff and dependants who may need evacuation or relocation, and the routes available.",
          "Single authorised spokesperson; everything else silent. Speculation in this band is dangerous rather than merely unhelpful.",
        ],
        spend: [
          "Hold cash. Draw available facilities early — availability in a conflict is not a given.",
          "Freeze all non-safety commitments immediately.",
          "Move balances and payment routes where sanctions or banking disruption are foreseeable.",
        ],
        projects: [
          "Suspend all construction; secure sites, plant and excavations against both damage and misuse.",
          "Account for and secure hazardous and fuel stocks.",
        ],
        contracts: [
          "Notify insurers immediately: war and allied perils are excluded or separately written in most policies, and the position must be established before it is relied on.",
          "Serve force majeure notices as the contracts require.",
          "Establish whether the State intends to requisition or direct the use of the aerodrome, and record any instruction in writing.",
        ],
        irreversible: "A person harmed. Everything else on this page can be rebuilt.",
      },
      {
        key: "72h-4w",
        band: "72 hours – 4 weeks",
        fromDays: 3,
        toDays: 28,
        posture: "minimum",
        decide: "Do we operate at all, and under whose authority and whose insurance?",
        operations: [
          "Operate only what the State permits and the insurance covers — both, not either.",
          "Support State, humanitarian and relief flights where directed; record every movement and every instruction.",
          "Segregate any military use from civil operations physically and procedurally, and promulgate what results.",
          "Reassess the security programme against the actual threat rather than the standing assessment.",
        ],
        people: [
          "Volunteer-only rosters for on-site work, with the risk stated plainly and accepted in writing.",
          "Hardship, evacuation and family support arrangements in place and funded.",
          "Retain security, fire, engineering and compliance whole; these are the roles that hold the floor.",
        ],
        spend: [
          "Weekly cash reporting to the executive, not monthly. The horizon has shortened and the reporting has to shorten with it.",
          "Pay critical suppliers and staff first and say so openly; the order of payment is a decision and should be a stated one.",
          "Reduce every cost not connected to safety, security or preservation.",
        ],
        projects: [
          "Stop everything. Secure and preserve; do not attempt to progress work under a conflict constraint.",
          "Protect drawings, records and design data — hold them off site and encrypted.",
        ],
        contracts: [
          "Confirm war-risk cover for any operation that continues, in writing, before it continues.",
          "Check every supply chain for sanctions exposure and for routes that no longer exist.",
          "Secure fuel, spares and consumables through alternative routes and accept the price; availability is the constraint.",
        ],
      },
      {
        key: "1-3m",
        band: "1–3 months",
        fromDays: 28,
        toDays: 90,
        posture: "closed",
        decide: "Do we preserve the airport for a reopening, or preserve the business for a reopening elsewhere?",
        operations: [
          "Hold closed to civil traffic with the certificate maintained, unless the State directs otherwise.",
          "Continue security, wildlife, obstacle and preservation regimes without interruption; these do not pause.",
          "Document damage and requisition continuously and to an evidential standard — it is the basis of every later claim.",
        ],
        people: [
          "Retain the minimum custodial establishment; relocate or release the rest with support.",
          "Keep paying where the business can. A workforce released without support does not come back, and the licensed part of it cannot be replaced quickly.",
          "Hold the competence matrix and fund currency training for everyone retained.",
        ],
        spend: [
          "Preservation and security budget only, protected from further cuts by a separate approval route.",
          "Seek State support, relief and compensation mechanisms; document the case continuously rather than reconstructing it afterwards.",
          "Model the runway date under several durations and report all of them, because the duration is unknowable and a single forecast will be believed.",
        ],
        projects: [
          "Cancel with the cancellation costs taken now, or convert to shelf-ready.",
          "Preserve the consents and designs; they are what makes a rebuild fast when it comes.",
        ],
        contracts: [
          "Dormancy agreements across the supplier base, with reactivation notice and price fixed.",
          "Renegotiate leases and concessions to a suspended position rather than terminating, where the counterparty will.",
          "Establish the claims position — insurance, State compensation, contractual relief — and appoint who owns each.",
        ],
        irreversible:
          "Losing the licensed workforce, the certificate or the specialist plant. Each turns a reopening into a rebuild, and in a conflict all three are lost by drift rather than by decision.",
      },
      {
        key: "3m+",
        band: "beyond 3 months, or no known end",
        fromDays: 90,
        toDays: null,
        posture: "care",
        decide:
          "What is the minimum expenditure that keeps a reopening possible at all, and are we still the right operator to make it?",
        operations: [
          "Care and maintenance: the estate preserved, the certificate held or formally suspended by agreement with the authority.",
          "Security maintained at the directed level regardless of posture. It is the last thing cut, not the first.",
          "Condition surveys continued on a reduced cycle, so the reopening cost is known rather than discovered.",
        ],
        people: [
          "Custodial team only. Named, contracted, supported, and small enough to fund for years rather than months.",
          "Maintain a register of released staff with their competences; it is the recruitment plan for the reopening.",
        ],
        spend: [
          "A multi-year preservation budget, approved as such, so it stops being renegotiated every quarter.",
          "Report to the board on three horizons — six months, eighteen months, indefinite — because planning to one is planning to be wrong.",
        ],
        projects: ["Nothing but preservation. Everything else shelf-ready or cancelled and said to be cancelled."],
        contracts: [
          "Long-dated dormancy with fixed reactivation terms; buy the option to restart, explicitly, and record what it costs.",
          "Maintain insurance for the unoccupied and closed position, reviewed annually against the actual state of the estate.",
          "Keep the State relationship live and documented; the reopening will be granted or withheld through it.",
        ],
        irreversible:
          "Surrendering the certificate outright. It may be right, and it should be a board decision with the recertification cost and lead time stated in the minute.",
      },
    ],
    restart: restartPhases({
      decide: [
        "Confirm the security position with the State and with the insurer, separately and in writing.",
        "Establish what was requisitioned, damaged or altered, and who bears the cost of putting it back.",
      ],
      estate: [
        "Survey for conflict-related damage and for unexploded ordnance before any crew works on the movement area.",
        "Re-survey the obstacle environment: construction, masts and debris may all have changed.",
      ],
      people: ["Support returning staff and those who were displaced; assume the workforce is not the one that left."],
      regulator: ["Expect a full audit rather than a check, and plan the lead time for one."],
    }),
    floor: REGULATORY_FLOOR,
  },
  {
    key: "airspace",
    name: "Airspace closed for an extended or unknown period",
    trigger:
      "the State, the military or the region closes the airspace serving the aerodrome: a conflict overhead, a political closure, a sustained navigation or surveillance failure, a volcanic or environmental exclusion",
    character:
      "the purest form of the problem. Every surface is serviceable, every system works, every person is at their post, and nothing can move. There is no operational remedy available to the operator at all, so the entire response is commercial, regulatory and human — which is precisely what an emergency plan does not cover.",
    holds: [
      "Chief executive",
      "Chief financial officer",
      "Chief operating officer",
      "ATC and State liaison",
      "Airline liaison",
      "Legal counsel",
    ],
    handover: AEP_HANDOVER,
    stages: [
      {
        key: "0-72h",
        band: "0–72 hours",
        fromDays: 0,
        toDays: 3,
        posture: "reduced",
        decide:
          "Is this hours, weeks or unknown? Ask the question explicitly on day one and record the answer, including 'we do not know'.",
        operations: [
          "Recover the aircraft, crews and passengers already on the ground; that is the whole of the first day.",
          "NOTAM the position and keep it current as the restriction changes.",
          "Hold the airfield at readiness — the restriction may lift as fast as it arrived, and in this band it often does.",
          "Open a single liaison channel to ATC and the State, with one named person on it.",
        ],
        people: [
          "Full establishment retained. Nothing structural in this band: the information is worst on day one and every decision taken on it will be wrong.",
          "Brief everyone daily with what is known and what is not. The second half matters more.",
          "Redeploy customer-facing staff to passenger welfare and to the deferred-maintenance backlog.",
        ],
        spend: [
          "Freeze discretionary spend and new commitments; nothing deeper yet.",
          "Confirm the cash position and the facility headroom today, and tell the board both.",
        ],
        projects: [
          "Continue work that does not need traffic; a closed airfield is the cheapest time to resurface a runway and the opportunity is usually missed.",
          "Bring forward any scheme whose cost is driven by working around traffic — this band is worth money if it is used.",
        ],
        contracts: [
          "Notify insurers and serve any contractual notices required.",
          "Tell handlers, concessions and fuel suppliers the same thing at the same time; a supply chain learning it piecemeal reacts worse.",
        ],
      },
      {
        key: "72h-4w",
        band: "72 hours – 4 weeks",
        fromDays: 3,
        toDays: 28,
        posture: "minimum",
        decide:
          "At what date does holding the full establishment stop being prudent and start being negligent? Name it now, before it arrives.",
        operations: [
          "Minimum safe operation: available for diversion and State flights on notice, no scheduled traffic.",
          "Mothball terminal areas properly — isolated, secured, inspected — rather than leaving them running empty.",
          "Use the closure: resurfacing, lighting renewal, systems replacement and survey work that traffic normally prevents.",
        ],
        people: [
          "Leave balances, flexible hours and voluntary unpaid leave first.",
          "Stop recruitment; defer offers with a review date rather than withdrawing them.",
          "Move staff to the maintenance and project work the closure has made possible; it is cheaper than idleness and it is worth something.",
        ],
        spend: [
          "Rebuild the budget at the minimum posture from the bottom up.",
          "Renegotiate utilities and standing charges to consumption on closed areas.",
          "Report the runway date weekly to the executive, monthly to the board.",
        ],
        projects: [
          "Split the programme into mandatory, deferrable and cancellable, and take the traffic-free opportunity on anything in the first two that benefits from it.",
          "Defer the rest with dated reviews.",
        ],
        contracts: [
          "Renegotiate the top suppliers by spend to volume-linked terms.",
          "Agree standby rates and reactivation notice with handling, fuel and catering.",
          "Move concessions to turnover-based rent with a floor; an empty terminal cannot pay fixed rent and a bankrupt concessionaire does not reopen with you.",
          "Record the reinstatement lead time for every contract varied. It is the input to the restart plan.",
        ],
      },
      {
        key: "1-3m",
        band: "1–3 months",
        fromDays: 28,
        toDays: 90,
        posture: "minimum",
        decide:
          "Hold the workforce or release it — and this is the decision the whole desk exists to bring forward, because taken late it is taken worse.",
        operations: [
          "Hold minimum safe operation while a reopening remains plausible; it is the posture from which restart is fastest.",
          "Keep one runway and the RFFS category available on a stated notice period, and promulgate the notice period honestly.",
          "Continue the preservation regime in full; the estate is the asset now.",
        ],
        people: [
          "Begin formal consultation on structural reductions to the statutory timetable, because that timetable is longer than the cash.",
          "Prefer short-time working, salary deferral and sabbatical to redundancy where a reopening within a year is plausible.",
          "Ring-fence the reopening cadre by name and fund their currency training.",
          "Publish the criteria for any reduction before applying them.",
        ],
        spend: [
          "Reforecast monthly across at least three durations and report all of them.",
          "Protect preservation, security and compliance spend behind a separate approval route.",
          "Cut every line not on the regulatory floor and not revenue-generating unless an owner argues for it in person.",
        ],
        projects: [
          "Cancel the cancellable now and take the cost while the counterparty is also motivated to settle.",
          "Re-profile committed schemes to longer, cheaper programmes.",
          "Finish anything that only a closure makes possible — leaving it half-done wastes the one benefit of the whole episode.",
        ],
        contracts: [
          "Approach lenders before a covenant is breached.",
          "Convert leases and concessions to suspended or turnover terms in exchange for term.",
          "Agree dormancy with reactivation notice and fixed price across the supplier base.",
          "Record termination cost and reinstatement cost side by side for every contract considered.",
        ],
        irreversible:
          "Redundancy, surrendered leases and cancelled schemes. Each is the right answer at some duration and the wrong answer at another, and the only defence is to write the assumed duration next to the decision.",
      },
      {
        key: "3m+",
        band: "beyond 3 months, or no known end",
        fromDays: 90,
        toDays: null,
        posture: "closed",
        decide:
          "Hold the certificate and the capability against an undated reopening, or preserve capital and accept that reopening becomes a project with a lead time measured in months.",
        operations: [
          "Close to traffic by NOTAM, certificate maintained, with a stated and honest reopening notice period.",
          "Full preservation regime on pavement, lighting, drainage, fuel and fixed plant, documented and inspected.",
          "Maintain the aeronautical information: a closed aerodrome still has to be correctly described.",
        ],
        people: [
          "Custodial establishment plus the reopening cadre, contracted and funded for the duration being planned to.",
          "Recurrent and currency training continues for everyone retained, without exception.",
          "Keep the register of released staff current; it is the reopening recruitment plan and it decays quickly.",
        ],
        spend: [
          "A closed-posture budget, set once and held, rather than a sequence of monthly reductions.",
          "Monthly to the board: runway date, burn, reopening cost and reopening lead time, together.",
          "Pursue State relief and compensation continuously, with the evidence assembled as it arises.",
        ],
        projects: [
          "Preservation only. Everything else shelf-ready with consents kept live, or cancelled and publicly so.",
        ],
        contracts: [
          "Long-dated dormancy with fixed reactivation price and notice for every critical service.",
          "Insurance reviewed specifically for the closed and unoccupied position.",
          "One contracted route back for every critical service, and a named owner for each.",
        ],
        irreversible:
          "Surrendering the certificate, losing the licensed workforce, disposing of specialist plant. Any may be right; none should happen without a board minute stating the recertification cost and lead time.",
      },
    ],
    restart: restartPhases({
      decide: [
        "Get the reopening date from the State in writing; a reopening planned against a rumour is a reopening planned twice.",
        "Confirm which airlines are actually returning, and at what volume, before committing to a posture.",
      ],
      estate: [
        "Close out the work done during the closure and prove it: a runway resurfaced under closure still needs its friction and its survey before traffic.",
      ],
      commercial: [
        "Rebuild slots from nothing; historic precedence may not survive a closure of this length.",
        "Expect the first schedule to be a fraction of the last one, and plan the posture for the fraction.",
      ],
    }),
    floor: REGULATORY_FLOOR,
  },
];

/* ── pricing the postures against the finance the platform holds ──────── */

export interface ContinuityInputs {
  now: Date;
  siteCode: string;
  finance: Finance | null;
  documents: DocumentRow[];
  compliance: ComplianceReport | null;
}

export interface PostureCost {
  posture: Posture;
  dailyCost: number | null;
  dailyRevenue: number | null;
  /** negative is a burn; positive would be a surplus, which none of these are */
  dailyNet: number | null;
  runwayDays: number | null;
  runsOutOn: string | null;
  sources: SourceRef[];
}

export interface Lever {
  key: string;
  label: string;
  value: string;
  /** null where the platform cannot price it — said, never guessed */
  amount: number | null;
  basis: string;
  sources: SourceRef[];
  assumed: boolean;
}

export interface ContinuityCheck {
  key: string;
  what: string;
  met: boolean;
  basis: string;
  todo?: string;
  sources: SourceRef[];
}

export interface ContinuityDesk {
  generatedAt: string;
  currency: string;
  /** where the money figures came from: a ledger, an entered set, or demonstration data */
  basis: Finance["basis"] | null;
  postures: PostureCost[];
  levers: Lever[];
  scenarios: ContinuityScenario[];
  checks: ContinuityCheck[];
  floor: FloorItem[];
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const DAY_MS = 86_400_000;

function addDays(from: Date, days: number): string {
  return new Date(from.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

function revenueTotal(f: Finance): number {
  return f.revenue.reduce((sum, line) => sum + line.actual, 0);
}

/**
 * What each posture costs a day, and how long the cash lasts there.
 *
 * The daily figures are the platform's own — the period's operating cost and
 * revenue divided by the days it covers — and the posture factor is the
 * assumption applied to them. Both are said, in that order, so a reader can
 * see exactly where the record stops and the planning begins.
 */
export function postureCosts(inp: ContinuityInputs): PostureCost[] {
  const f = inp.finance;
  if (!f || f.daysElapsed <= 0) return POSTURES.map((p) => blankPosture(p));

  const days = f.daysElapsed;
  const dailyOpex = f.opex.actual / days;
  const dailyRevenue = revenueTotal(f) / days;
  const cash = f.cash?.balance != null ? f.cash.balance + (f.cash.facility ?? 0) : null;

  const sources: SourceRef[] = [
    {
      kind: "finance",
      ref: f.period,
      label: t("operating cost and revenue for {period}", { period: f.period }),
      href: "/cash",
    },
    ...(f.cash?.balance != null
      ? [{ kind: "finance" as const, ref: "cash", label: t("cash balance and facility"), href: "/cash" }]
      : []),
  ];

  return POSTURES.map((p) => {
    const dailyCost = dailyOpex * p.opexFactor;
    const dailyRev = dailyRevenue * p.revenueFactor;
    const net = dailyRev - dailyCost;
    const burning = net < 0;
    const runwayDays = cash != null && burning ? Math.floor(cash / -net) : null;
    return {
      posture: p,
      dailyCost: Math.round(dailyCost),
      dailyRevenue: Math.round(dailyRev),
      dailyNet: Math.round(net),
      runwayDays,
      runsOutOn: runwayDays != null ? addDays(inp.now, runwayDays) : null,
      sources,
    };
  });
}

function blankPosture(p: Posture): PostureCost {
  return {
    posture: p,
    dailyCost: null,
    dailyRevenue: null,
    dailyNet: null,
    runwayDays: null,
    runsOutOn: null,
    sources: [],
  };
}

/**
 * The levers, priced where the platform can price them and named where it cannot.
 *
 * Two of these are real arithmetic over records — the uncommitted capital
 * programme, and the operating cost of the largest area in the ledger's own
 * split. The others are not priced at all, and say so. A workforce reduction
 * has a number, and this platform does not hold it: inventing one so the table
 * looks complete would be the most damaging thing on this screen, because it
 * is the figure somebody would act on.
 */
export function levers(inp: ContinuityInputs): Lever[] {
  const f = inp.finance;
  const cur = f?.currency ?? "SAR";
  const out: Lever[] = [];

  const uncommitted = (f?.capex ?? []).reduce((sum, c) => sum + Math.max(0, c.budget - c.spent), 0);
  out.push({
    key: "capex",
    label: t("Stop the capital program where it is not yet spent"),
    value: uncommitted > 0 ? money(Math.round(uncommitted), cur) : t("nothing uncommitted on the platform"),
    amount: uncommitted > 0 ? Math.round(uncommitted) : null,
    basis: t(
      "budget less spent across {n} on the platform. Not all of it is cancellable — committed work carries a cancellation cost the platform does not hold — so this is the ceiling, not the saving.",
      { n: tn((f?.capex ?? []).length, "{n} scheme", "{n} schemes") },
    ),
    sources: [{ kind: "finance", ref: "capex", label: t("the capital program"), href: "/capex" }],
    assumed: false,
  });

  const areas = [...(f?.opexByArea ?? [])].sort((a, b) => b.actual - a.actual);
  const largest = areas[0];
  if (largest && f) {
    const perDay = largest.actual / Math.max(1, f.daysElapsed);
    out.push({
      key: "area",
      label: t("Close or suspend the largest operating area — {area}", { area: largest.label }),
      value: t("{amount} a day", { amount: money(Math.round(perDay), cur) }),
      amount: Math.round(perDay),
      basis: t(
        "{area} is the largest line in the ledger's own split at {total} for {period}. How much of it can actually stop is an operating judgment, not a figure.",
        { area: largest.label, total: money(Math.round(largest.actual), cur), period: f.period },
      ),
      sources: [{ kind: "finance", ref: largest.key, label: largest.label, href: "/cash" }],
      assumed: false,
    });
  }

  out.push({
    key: "people",
    label: t("Reduce the workforce"),
    value: t("not priced here"),
    amount: null,
    basis: t(
      "the platform holds no establishment, no salary cost and no statutory notice periods, so it cannot price this and does not estimate it. It is normally the largest lever and the slowest, which is why the plan starts consultation in the 1–3 month band rather than after it.",
    ),
    sources: [],
    assumed: false,
  });

  out.push({
    key: "contracts",
    label: t("Renegotiate contracts to standby or dormancy"),
    value: t("not priced here"),
    amount: null,
    basis: t(
      "the platform holds no contract register, so neither the standing charges nor the reactivation costs can be read. Recording both against each contract is what would make this lever computable.",
    ),
    sources: [],
    assumed: false,
  });

  return out;
}

/* ── is the airport ready for a long one ──────────────────────────────── */

function issuedDoc(inp: ContinuityInputs, match: string): DocumentRow | undefined {
  return inp.documents.find(
    (d) =>
      d.status === "issued" &&
      (d.title.toLowerCase().includes(match) || d.tags.some((tag) => tag.toLowerCase().includes(match))),
  );
}

export function continuityChecks(inp: ContinuityInputs): ContinuityCheck[] {
  const out: ContinuityCheck[] = [];

  const aep = issuedDoc(inp, "emergency");
  out.push({
    key: "aep",
    what: t("the aerodrome emergency plan is issued"),
    met: !!aep,
    basis: aep ? t("in the library: {title}", { title: aep.title }) : t("no issued emergency plan in the library"),
    todo: aep ? undefined : t("issue it — every scenario here hands over from it"),
    sources: aep
      ? [{ kind: "document", ref: aep.reference ?? aep.id, label: aep.title, href: `/library/${aep.id}` }]
      : [],
  });

  const bcp = issuedDoc(inp, "continuity");
  out.push({
    key: "bcp",
    what: t("a business continuity plan is issued, and it covers a closure beyond three months"),
    met: !!bcp,
    basis: bcp
      ? t("in the library: {title}", { title: bcp.title })
      : t(
          "no issued continuity plan in the library. The emergency plan does not cover this: it is written for an event that ends.",
        ),
    todo: bcp
      ? undefined
      : t("adopt this desk's scenarios as a draft, have them approved, and issue them in the library"),
    sources: bcp
      ? [{ kind: "document", ref: bcp.reference ?? bcp.id, label: bcp.title, href: `/library/${bcp.id}` }]
      : [],
  });

  const exercise = inp.compliance?.obligations.find((o) => o.title.toLowerCase().includes("emergency exercise"));
  out.push({
    key: "exercise",
    what: t("the emergency exercise is current"),
    met: exercise?.status === "met",
    basis: exercise
      ? t("{title}: {status}", { title: exercise.title, status: t(exercise.status) })
      : t("no emergency exercise obligation on the register"),
    todo:
      exercise?.status === "met"
        ? undefined
        : t("run it, and exercise a prolonged scenario rather than only a sudden one"),
    sources: exercise
      ? [{ kind: "obligation", ref: exercise.periodKey ?? exercise.title, label: exercise.title, href: "/compliance" }]
      : [],
  });

  const f = inp.finance;
  out.push({
    key: "finance",
    what: t("the money behind these decisions is a record rather than a demonstration"),
    met: !!f && f.basis !== "demo",
    basis: !f
      ? t("no finance on the platform")
      : f.basis === "ledger"
        ? t("read from a finance export")
        : f.basis === "entered"
          ? t("entered by {who}", { who: f.enteredBy ?? t("a named person") })
          : t("demonstration figures — every runway date on this desk is illustrative"),
    todo:
      !f || f.basis === "demo"
        ? t("import the ledger or enter the figures, so the runway dates mean something")
        : undefined,
    sources: f
      ? [{ kind: "finance", ref: f.period, label: t("finance, {period}", { period: f.period }), href: "/cash" }]
      : [],
  });

  out.push({
    key: "cash",
    what: t("the cash balance and the facility are on the platform"),
    met: f?.cash?.balance != null,
    basis:
      f?.cash?.balance != null
        ? t("balance and facility recorded")
        : t("no cash position, so no posture below can carry a date"),
    todo: f?.cash?.balance != null ? undefined : t("record the cash balance and the facility"),
    sources: f?.cash?.balance != null ? [{ kind: "finance", ref: "cash", label: t("cash"), href: "/cash" }] : [],
  });

  return out;
}

/* ── the desk ─────────────────────────────────────────────────────────── */

export function buildContinuityDesk(inp: ContinuityInputs): ContinuityDesk {
  const f = inp.finance;
  const cur = f?.currency ?? "SAR";
  const costs = postureCosts(inp);
  const levered = levers(inp);
  const checks = continuityChecks(inp);

  const at = (key: PostureKey) => costs.find((c) => c.posture.key === key) ?? null;
  const full = at("full");
  const closed = at("closed");
  const minimum = at("minimum");
  const uncommitted = levered.find((l) => l.key === "capex")?.amount ?? null;
  const bcp = checks.find((c) => c.key === "bcp");
  const demo = !f || f.basis === "demo";

  const financeSource: SourceRef[] = f
    ? [{ kind: "finance", ref: f.period, label: t("finance, {period}", { period: f.period }), href: "/cash" }]
    : [];

  const tiles: ContinuityDesk["tiles"] = [
    {
      key: "burn",
      label:
        full?.dailyNet != null && full.dailyNet >= 0
          ? t("net surplus, operating normally")
          : t("net burn, operating normally"),
      value: full?.dailyNet != null ? Math.abs(full.dailyNet) : null,
      unit: t("a day"),
      text:
        full?.dailyNet != null
          ? t("{cost} out, {rev} in, for {period}", {
              cost: money(full.dailyCost ?? 0, cur),
              rev: money(full.dailyRevenue ?? 0, cur),
              period: f?.period ?? "",
            })
          : t("no finance on the platform"),
      tone: full?.dailyNet != null && full.dailyNet < 0 ? "warn" : "good",
      assumed: demo,
      sources: financeSource,
    },
    {
      key: "runway-now",
      label: t("cash runway, operating normally"),
      value: full?.runwayDays ?? null,
      unit: t("days"),
      text: full?.runsOutOn
        ? t("runs out {date} if nothing changes", { date: full.runsOutOn })
        : t("no cash position recorded"),
      tone: full?.runwayDays != null && full.runwayDays < 180 ? "bad" : "warn",
      assumed: demo,
      sources: financeSource,
    },
    {
      key: "runway-closed",
      label: t("cash runway, closed to traffic"),
      value: closed?.runwayDays ?? null,
      unit: t("days"),
      text: closed?.runsOutOn
        ? t("runs out {date} — the reason to decide early", { date: closed.runsOutOn })
        : t("no cash position recorded"),
      tone: "warn",
      assumed: true,
      sources: financeSource,
    },
    {
      key: "bought",
      label: t("days gained by closing rather than holding readiness"),
      /*
       * Closed against minimum, not against full.
       *
       * Full posture is the wrong comparison and on a profitable airport it is
       * not computable at all — operating normally never runs out until it
       * stops. The decision that is actually taken in the 1-3 month band is
       * between holding minimum safe operation, ready for diversions and State
       * flights, and closing to traffic. On this estate's own figures that
       * comparison is the finding: readiness is not cheap, and holding it is
       * frequently dearer than closing, because the fire cover and the crews
       * stay while the revenue does not.
       */
      value: minimum?.runwayDays != null && closed?.runwayDays != null ? closed.runwayDays - minimum.runwayDays : null,
      unit: t("days"),
      text:
        minimum?.runwayDays != null && closed?.runwayDays != null
          ? closed.runwayDays > minimum.runwayDays
            ? t("closing to traffic rather than holding minimum safe operation")
            : t("holding minimum safe operation is the cheaper of the two here")
          : t("not computable without a cash position"),
      tone: "good",
      assumed: true,
      sources: financeSource,
    },
    {
      key: "capex",
      label: t("capital not yet spent"),
      value: uncommitted,
      unit: cur,
      text: uncommitted != null ? t("the ceiling on what stopping the program preserves") : t("no capital program"),
      tone: "good",
      assumed: false,
      sources: [{ kind: "finance", ref: "capex", label: t("the capital program"), href: "/capex" }],
    },
    {
      key: "plan",
      label: t("continuity plan issued"),
      value: null,
      unit: "",
      text: bcp?.met ? t("in the library") : t("not in the library — these scenarios are a draft, not an adopted plan"),
      tone: bcp?.met ? "good" : "bad",
      sources: bcp?.sources ?? [],
    },
  ];

  const boardLine: string[] = [];
  if (full?.dailyNet != null) {
    // A surplus is not a burn. Calling it one would be the easiest sentence
    // to write here and the most misleading: an airport that is profitable
    // operating normally has no runway limit at all until it stops.
    boardLine.push(
      full.dailyNet < 0
        ? t(
            "At {site}, operating normally costs {cost} a day against {rev} of revenue — a net burn of {net} a day{basis}.",
            {
              site: inp.siteCode,
              cost: money(full.dailyCost ?? 0, cur),
              rev: money(full.dailyRevenue ?? 0, cur),
              net: money(Math.abs(full.dailyNet), cur),
              basis: demo ? t(", on demonstration figures") : "",
            },
          )
        : t(
            "At {site}, operating normally earns {rev} a day against {cost} of cost — a surplus of {net} a day{basis}. The cash question only begins when the operation stops.",
            {
              site: inp.siteCode,
              cost: money(full.dailyCost ?? 0, cur),
              rev: money(full.dailyRevenue ?? 0, cur),
              net: money(full.dailyNet, cur),
              basis: demo ? t(", on demonstration figures") : "",
            },
          ),
    );
  } else {
    boardLine.push(t("No finance is on the platform, so nothing on this desk carries a date."));
  }
  if (full?.runwayDays != null && closed?.runwayDays != null) {
    boardLine.push(
      t(
        "The cash covers {now} operating normally and {shut} closed to traffic: closing buys {bought}, and the cost of deciding late is the difference.",
        {
          now: tn(full.runwayDays, "{n} day", "{n} days"),
          shut: tn(closed.runwayDays, "{n} day", "{n} days"),
          bought: tn(closed.runwayDays - full.runwayDays, "{n} day", "{n} days"),
        },
      ),
    );
  }
  if (minimum?.runwayDays != null) {
    // The posture the 1-3 month band actually sits at, and therefore the only
    // runway figure the workforce decision is taken against.
    boardLine.push(
      t(
        "Held at minimum safe operation — diversions and State flights, no schedule — the cash covers {days}, to {date}. That is the horizon the workforce decision is taken against.",
        {
          days: tn(minimum.runwayDays, "{n} day", "{n} days"),
          date: minimum.runsOutOn ?? "—",
        },
      ),
    );
  }
  if (minimum?.runwayDays != null && closed?.runwayDays != null && closed.runwayDays > minimum.runwayDays) {
    // Counter-intuitive and worth saying out loud: standing ready costs more
    // than shutting, because the fire cover and the crews stay while the
    // revenue does not. An executive who has not seen the arithmetic will
    // assume the opposite.
    boardLine.push(
      t(
        "Holding minimum safe operation costs more than closing: readiness runs out {early} earlier than a closure would, because the fire cover and the crews stay while the revenue does not. Readiness is a purchase, and this is its price.",
        { early: tn(closed.runwayDays - minimum.runwayDays, "{n} day", "{n} days") },
      ),
    );
  }
  boardLine.push(
    t(
      "The decision that cannot be deferred is the workforce, and the plan brings it forward into the 1–3 month band because the statutory consultation timetable is longer than the cash.",
    ),
  );
  if (!bcp?.met) {
    boardLine.push(
      t(
        "There is no issued continuity plan in the library. The aerodrome emergency plan does not cover this — it is written for an event that ends — so these scenarios are a draft for approval, not a plan in force.",
      ),
    );
  }

  const gaps: string[] = [];
  if (!f) gaps.push(t("no finance on the platform: every posture here is unpriced"));
  else if (f.basis === "demo") gaps.push(t("the finance is demonstration data, so every runway date is illustrative"));
  if (f && f.cash?.balance == null) gaps.push(t("no cash balance recorded, so no posture carries a date"));
  if (!(f?.opexByArea ?? []).length)
    gaps.push(t("the operating cost is not split by area, so spend cuts cannot be named against a line"));
  gaps.push(
    t(
      "the posture factors are planning assumptions, not records — an airport that has never run at minimum posture has no measurement of what it costs",
    ),
  );
  gaps.push(
    t("the workforce and the contract register are not on the platform, so two of the four levers cannot be priced"),
  );

  const material = materialForContinuity({
    tiles,
    postures: costs,
    levers: levered,
    checks,
    boardLine,
    scenarios: CONTINUITY_SCENARIOS,
  });
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));

  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    basis: f?.basis ?? null,
    postures: costs,
    levers: levered,
    scenarios: CONTINUITY_SCENARIOS,
    checks,
    floor: REGULATORY_FLOOR,
    tiles,
    boardLine,
    gaps,
    figures,
  };
}

export const CONTINUITY_MODEL_PROMPT = [
  "You are advising an airport's chief executive on a prolonged disruption — force majeure, conflict, or airspace closed for an unknown period — from the material given and nothing else.",
  "You may explain, prioritise and argue. You must not state any number that is not in the material.",
  "Every posture cost rests on a planning assumption applied to the platform's finance; say so whenever you use one.",
  "Never state or imply that the aerodrome is safe to operate, or fit to reopen. That is the accountable manager's decision.",
  "Be brief and direct.",
].join(" ");

export function materialForContinuity(
  d: Pick<ContinuityDesk, "tiles" | "postures" | "levers" | "checks" | "boardLine" | "scenarios">,
): string {
  const L: string[] = [];
  for (const tile of d.tiles)
    L.push(
      `Tile ${tile.label}: ${tile.value ?? "n/a"} ${tile.unit} — ${tile.text}${tile.assumed ? " [assumption applied]" : ""}`,
    );
  for (const p of d.postures)
    L.push(
      `Posture ${p.posture.name} (${Math.round(p.posture.opexFactor * 100)}% of cost, ${Math.round(p.posture.revenueFactor * 100)}% of revenue — assumption): ` +
        (p.dailyNet != null
          ? `${p.dailyCost} out, ${p.dailyRevenue} in, net ${p.dailyNet} a day; runway ${p.runwayDays ?? "unknown"} days${p.runsOutOn ? `, to ${p.runsOutOn}` : ""}.`
          : "not priced — no finance."),
    );
  for (const l of d.levers) L.push(`Lever ${l.label}: ${l.value} — ${l.basis}`);
  for (const c of d.checks)
    L.push(`${c.met ? "met" : "GAP"}: ${c.what} — ${c.basis}${c.todo ? ` — to do: ${c.todo}` : ""}`);
  for (const s of d.scenarios) {
    L.push(`Scenario ${s.key} "${s.name}": ${s.character}`);
    for (const st of s.stages)
      L.push(
        `  ${st.band} at ${POSTURE_BY_KEY[st.posture].name}: ${st.decide} People: ${st.people.length}. Spend: ${st.spend.length}. Projects: ${st.projects.length}. Contracts: ${st.contracts.length}.${st.irreversible ? ` Irreversible: ${st.irreversible}` : ""}`,
      );
    L.push(`  Restart: ${s.restart.map((r) => `${r.name} (T-${r.leadDays}d)`).join(", ")}`);
  }
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerContinuity(q: string, d: ContinuityDesk): string | null {
  const text = q.toLowerCase();
  const cur = d.currency;

  if (/runway|how long|cash last|run out|solven/.test(text)) {
    const lines = d.postures
      .filter((p) => p.runwayDays != null)
      .map((p) =>
        t("{name}: {days}, to {date}", {
          name: p.posture.name,
          days: tn(p.runwayDays ?? 0, "{n} day", "{n} days"),
          date: p.runsOutOn ?? "—",
        }),
      );
    return lines.length
      ? `${lines.join("\n")}\n${t("Each rests on a planning assumption about what the posture costs, not on a measurement.")}`
      : t("No cash position is on the platform, so no posture carries a date.");
  }
  if (/shut ?down|close|posture|minimum|care and maintenance/.test(text)) {
    return d.postures
      .map((p) =>
        t("{name} — {what}. {cost} a day{runway}.", {
          name: p.posture.name,
          what: p.posture.what,
          cost: p.dailyCost != null ? money(p.dailyCost, cur) : t("not priced"),
          runway: p.runwayDays != null ? t(", runway {days}", { days: tn(p.runwayDays, "{n} day", "{n} days") }) : "",
        }),
      )
      .join("\n");
  }
  if (/restart|reopen|recover|resum/.test(text)) {
    const s = d.scenarios[0];
    return s.restart
      .map((r) => t("T-{days} {name}: {gate}", { days: r.leadDays, name: r.name, gate: r.gate }))
      .join("\n");
  }
  if (/lever|cut|saving|reduce|renegoti|downsiz/.test(text)) {
    return d.levers.map((l) => `${l.label}: ${l.value} — ${l.basis}`).join("\n");
  }
  if (/floor|must continue|still have to|regulat/.test(text)) {
    return d.floor.map((f) => `${f.what} — ${f.why} (${f.reference})`).join("\n");
  }
  if (/board|summar/.test(text)) return d.boardLine.join(" ");
  const scenario = d.scenarios.find((s) => text.includes(s.key) || text.includes(s.name.toLowerCase().slice(0, 10)));
  if (scenario) {
    return `${scenario.name}: ${scenario.character}\n${scenario.stages
      .map((st) => `${st.band} — ${POSTURE_BY_KEY[st.posture].name}: ${st.decide}`)
      .join("\n")}`;
  }
  return null;
}
